import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ALL_TYPES, type CreativeType, type Term } from "@acs/shared";
import { api } from "../api";

export function ProductSettings({ productId }: { productId: string }) {
  const [product, setProduct] = useState<any | null>(null);
  const [terms, setTerms] = useState<Term[]>([]);
  const [newTerm, setNewTerm] = useState("");
  const [types, setTypes] = useState<CreativeType[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  async function reload() {
    const p = await api.getProduct(productId);
    setProduct(p.product);
    setTypes((p.product.types ?? []) as CreativeType[]);
    const t = await api.listTerms(productId);
    setTerms(t.terms);
  }

  useEffect(() => {
    reload();
  }, [productId]);

  async function addTerm() {
    if (!newTerm) return;
    await api.createTerm(productId, newTerm);
    setNewTerm("");
    reload();
  }

  async function removeTerm(id: string) {
    await api.deleteTerm(id);
    reload();
  }

  const [savingTypes, setSavingTypes] = useState(false);
  async function saveTypes(next: CreativeType[]) {
    setTypes(next);
    setSavingTypes(true);
    try {
      await api.updateProduct(productId, { types: next });
    } finally {
      setSavingTypes(false);
    }
  }

  async function doDelete() {
    setDeleting(true);
    try {
      await api.deleteProduct(productId);
      window.dispatchEvent(new Event("products:changed"));
      navigate("/");
    } finally {
      setDeleting(false);
    }
  }

  if (!product) return <div className="p-8 text-zinc-500">Yükleniyor…</div>;

  return (
    <div className="p-8 max-w-2xl space-y-6">
      <section className="bg-white border rounded p-5">
        <h3 className="font-bold mb-3">Ürün Bilgisi</h3>
        <div className="text-sm space-y-1">
          <div>
            <span className="text-zinc-500">Ad: </span>
            {product.name}
          </div>
          <div>
            <span className="text-zinc-500">Tag: </span>
            <span className="font-mono">{product.tag}</span>
          </div>
          <div>
            <span className="text-zinc-500">Drive Klasör: </span>
            <span className="font-mono text-xs">{product.drive_folder_id}</span>
          </div>
        </div>
      </section>

      <section className="bg-white border rounded p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold">Aktif Type Seti</h3>
          {savingTypes && <span className="text-xs text-zinc-500">kaydediliyor…</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          {ALL_TYPES.map((t) => {
            const on = types.includes(t);
            return (
              <button
                key={t}
                disabled={savingTypes}
                onClick={() =>
                  saveTypes(on ? types.filter((x) => x !== t) : [...types, t])
                }
                className={`px-3 py-1 text-sm rounded border disabled:opacity-50 ${
                  on
                    ? "bg-accent text-white border-accent"
                    : "bg-white text-zinc-700 border-zinc-300"
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>
      </section>

      <section className="bg-white border rounded p-5">
        <h3 className="font-bold mb-3">Termin Listesi</h3>
        <div className="flex gap-2 mb-3">
          <input
            value={newTerm}
            onChange={(e) => setNewTerm(e.target.value)}
            placeholder="ADHD"
            className="flex-1 border rounded px-2 py-1.5 text-sm"
            onKeyDown={(e) => e.key === "Enter" && addTerm()}
          />
          <button
            onClick={addTerm}
            className="px-3 py-1.5 rounded bg-accent text-white text-sm"
          >
            Ekle
          </button>
        </div>
        <ul className="space-y-1">
          {terms.map((t) => (
            <li
              key={t.id}
              className="flex justify-between items-center text-sm py-1"
            >
              <span>{t.name}</span>
              <button
                onClick={() => removeTerm(t.id)}
                className="text-xs text-red-600 hover:underline"
              >
                sil
              </button>
            </li>
          ))}
          {terms.length === 0 && (
            <li className="text-xs text-zinc-400">Henüz termin yok.</li>
          )}
        </ul>
      </section>

      <section className="bg-white border border-red-200 rounded p-5">
        <h3 className="font-bold mb-1 text-red-700">Tehlikeli bölge</h3>
        <p className="text-xs text-zinc-600 mb-3">
          Bu işlem ürünü, tüm terminlerini, jobs/medya kayıtlarını ve cost
          geçmişini geri alınamaz şekilde siler. Drive'daki dosyalara dokunmaz.
        </p>
        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            className="px-3 py-1.5 rounded border border-red-300 text-red-700 text-sm hover:bg-red-50"
          >
            Ürünü sil…
          </button>
        ) : (
          <div className="space-y-2">
            <div className="text-sm text-red-700">
              Emin misin? <span className="font-mono">{product.tag}</span> ürünü ve
              tüm bağlı kayıtları silinecek.
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="px-3 py-1.5 rounded border text-sm"
              >
                Vazgeç
              </button>
              <button
                onClick={doDelete}
                disabled={deleting}
                className="px-3 py-1.5 rounded bg-red-600 text-white text-sm disabled:opacity-50"
              >
                {deleting ? "Siliniyor…" : "Evet, sil"}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
